import { AreaPayload, AreaResponse, CityStateDropDownResponse } from '../../type/Area';
import axiosInstance from '../axios';
import { useMutation, useQuery } from '@tanstack/react-query';
import { clearPageCache, getPageCache, putPageCache } from '../../utils/cache';

// Create Area
const createArea = async (payload: AreaPayload): Promise<AreaResponse> => {
	const response = await axiosInstance.post<AreaResponse>('/area/createArea', payload);
	clearPageCache('area_list');
	return response.data;
};

export const useCreateArea = () => {
	return useMutation<AreaResponse, Error, AreaPayload>({
		mutationFn: createArea,
	});
};

// Update Area
const updateAreaById = async (payload: AreaPayload): Promise<AreaResponse> => {
	const response = await axiosInstance.post<AreaResponse>('/area/updateAreaById', payload);
	clearPageCache('area_list');
	return response.data;
};

export const useUpdateAreaById = () => {
	return useMutation<AreaResponse, Error, AreaPayload>({
		mutationFn: updateAreaById,
	});
};

// Get Area By Id
const getAreaById = async (payload: AreaPayload): Promise<AreaResponse> => {
	if (getPageCache('area_list', payload)) {
		return getPageCache('area_list', payload);
	}
	const response = await axiosInstance.post<AreaResponse>('/area/getAreaById', payload);
	putPageCache('area_list', payload, response.data.data);
	return response.data;
};

export const useGetAreaById = () => {
	return useMutation<AreaResponse, Error, AreaPayload>({
		mutationKey: ['getAreaById'],
		mutationFn: getAreaById,
	});
};

export const useQGetAreaById = (payload: any | null) => {
	return useQuery<AreaResponse, Error, any>({
		queryKey: ['getQAreaById', payload],
		queryFn: () => getAreaById(payload),
		enabled: !!payload?._id, // ✅ only run when _id is defined and truthy
	});
};
// Deactive Area By Id
const deActiveAreaById = async (payload: AreaPayload): Promise<AreaResponse> => {
	const response = await axiosInstance.post<AreaResponse>('/area/deactiveAreaById', payload);
	clearPageCache('area_list');
	return response.data;
};

export const useDeActiveAreaById = () => {
	return useMutation<AreaResponse, Error, AreaPayload>({
		mutationFn: deActiveAreaById,
	});
};

// Active Area By Id
const activeAreaById = async (payload: AreaPayload): Promise<AreaResponse> => {
	const response = await axiosInstance.post<AreaResponse>('/area/activeAreaById', payload);
	clearPageCache('area_list');
	return response.data;
};

export const useActiveAreaById = () => {
	return useMutation<AreaResponse, Error, AreaPayload>({
		mutationFn: activeAreaById,
	});
};

// List Area
const listAreas = async (payload: AreaPayload): Promise<AreaResponse> => {
	if (getPageCache('area_list', payload)) {
		return getPageCache('area_list', payload);
	}
	const response: any = await axiosInstance.post<AreaResponse>('/area/listAreas', payload);
	putPageCache('area_list', payload, response.data.data);
	return response.data.data;
};

export const useListAreas = (payload: AreaPayload) => {
	return useQuery<AreaResponse, Error>({
		queryKey: ['listAreas', payload],
		queryFn: () => listAreas(payload),
		enabled: !!payload, // Optional: disables query until payload is available
	});
};

// Search Area
const searchAreas = async (payload: AreaPayload): Promise<AreaResponse> => {
	const response = await axiosInstance.post<AreaResponse>('/area/searchAreas', payload);
	return response.data;
};

export const useSearchAreas = () => {
	return useMutation<AreaResponse, Error, AreaPayload>({
		mutationFn: searchAreas,
	});
};

// Dropdown City
// memory cache store
// let cityDropdownCache: CityStateDropDownResponse | null = null;
// const dropdownListCity = async (): Promise<CityStateDropDownResponse> => {
// 	if (cityDropdownCache) {
// 		// Return from memory cache
// 		return cityDropdownCache;
// 	}

// 	const response = await axiosInstance.post('/util/queryCity', {});
// 	cityDropdownCache = response.data; // store in cache
// 	return response.data;
// };

// export const useDropdownListCity = () => {
// 	return useQuery({
// 		queryKey: ['queryCity'],
// 		queryFn: dropdownListCity,
// 	});
// };
const dropdownListCity = async (payload: AreaPayload): Promise<CityStateDropDownResponse> => {
	const response = await axiosInstance.post('/util/queryCity', payload);
	return response.data;
};

export const useDropdownListCity = () => {
	return useMutation<CityStateDropDownResponse, Error, AreaPayload>({
		mutationKey: ['queryCity'],
		mutationFn: dropdownListCity,
	});
};

// Dropdown State
// memory cache store
// let stateDropdownCache: CityStateDropDownResponse | null = null;
// const dropdownListState = async (): Promise<CityStateDropDownResponse> => {
// 	if (stateDropdownCache) {
// 		// Return from memory cache
// 		return stateDropdownCache;
// 	}

// 	const response = await axiosInstance.post('/util/queryState', {});
// 	stateDropdownCache = response.data; // store in cache
// 	return response.data;
// };

// export const useDropdownListState = () => {
// 	return useQuery({
// 		queryKey: ['queryState'],
// 		queryFn: dropdownListState,
// 	});
// };
const dropdownListState = async (payload: AreaPayload): Promise<CityStateDropDownResponse> => {
	const response = await axiosInstance.post('/util/queryState', payload);
	return response.data;
};

export const useDropdownListState = () => {
	return useMutation<CityStateDropDownResponse, Error, AreaPayload>({
		mutationKey: ['queryState'],
		mutationFn: dropdownListState,
	});
};
// Country Dropdown
const dropdownListCountry = async (payload: AreaPayload): Promise<CityStateDropDownResponse> => {
	const response = await axiosInstance.post('/util/queryCountry', payload);
	return response.data;
};

export const useDropdownListCountry = () => {
	return useMutation<CityStateDropDownResponse, Error, AreaPayload>({
		mutationKey: ['queryCountry'],
		mutationFn: dropdownListCountry,
	});
};
